import { Boundaries } from "@planzone/core";
import { group } from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { reduceResourceGroup } from "../../common/resourceGroup";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(
  state: State,
  { groupId, resourceType }: Boundaries["retrieveResourceGroup"]["request"],
): State {
  const index = state.findIndex((req) => req.id === groupId);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          requestState: setRequestStateToPending(),
          id: groupId,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          group: { ...group, uuid: groupId, resourceType },
          requestState: setRequestStateToPending(),
          id: groupId,
        },
      ];
}

function handleSuccess(
  state: State,
  { group, resourceType }: Boundaries["retrieveResourceGroup"]["success"],
): State {
  const index = state.findIndex((req) => req.id === group.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          group: { ...group, resourceType },
          requestState: setRequestStateToSuccess(),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          group: { ...group, resourceType },
          requestState: setRequestStateToSuccess(),
          id: group.uuid,
        },
      ];
}

function handleFailure(
  state: State,
  { groupId, resourceType }: Boundaries["retrieveResourceGroup"]["failure"],
): State {
  const index = state.findIndex((req) => req.id === groupId);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          requestState: setRequestStateToFailure(),
          id: groupId,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          group: { ...group, uuid: groupId, resourceType },
          requestState: setRequestStateToFailure(),
          id: groupId,
        },
      ];
}

function handleResourceGroupEdition(
  state: State,
  group: Boundaries["editResourceGroup"]["success"],
): State {
  return state.map((res) =>
    res.group.uuid === group.uuid ? { ...res, group } : res,
  );
}

function handleResourceToResourceGroupAdditionRequest(
  state: State,
  { resource, group }: Boundaries["addResourceToResourceGroup"]["request"],
): State {
  return state.map((res) =>
    res.group.uuid === group.uuid
      ? {
          ...res,
          group: { ...group, resources: [...group.resources, resource] },
        }
      : res,
  );
}

function handleResourceToResourceGroupAdditionFailure(
  state: State,
  { resource, group }: Boundaries["addResourceToResourceGroup"]["failure"],
): State {
  return state.map((res) =>
    res.group.uuid === group.uuid
      ? {
          ...res,
          group: {
            ...group,
            resources: group.resources.filter(
              ({ uuid }) => uuid !== resource.uuid,
            ),
          },
        }
      : res,
  );
}

function handleResourceToResourceGroupRemovalRequest(
  state: State,
  { resource, group }: Boundaries["removeResourceFromResourceGroup"]["request"],
): State {
  return state.map((res) =>
    res.group.uuid === group.uuid
      ? {
          ...res,
          group: {
            ...group,
            resources: group.resources.filter(
              ({ uuid }) => uuid !== resource.uuid,
            ),
          },
        }
      : res,
  );
}

function handleResourceToResourceGroupRemovalFailure(
  state: State,
  { resource, group }: Boundaries["removeResourceFromResourceGroup"]["failure"],
): State {
  return state.map((res) =>
    res.group.uuid === group.uuid
      ? {
          ...res,
          group: {
            ...group,
            resources: group.resources,
          },
        }
      : res,
  );
}

function handleRemoveResourceFromProjectRequest(
  state: State,
  { projectResource }: Boundaries["removeResourceFromProject"]["request"],
): State {
  return state.map((res) => ({
    ...res,
    group: {
      ...res.group,
      resources: group.resources.filter(
        (resource) => resource.resource.uuid !== projectResource.resource.uuid,
      ),
    },
  }));
}

function handleRemoveResourceFromProjectFailure(
  state: State,
  { projectResource }: Boundaries["removeResourceFromProject"]["failure"],
): State {
  return state.map((res) => ({
    ...res,
    group: {
      ...res.group,
      resources: group.resources,
    },
  }));
}

function handleRemoveResourceFromPlanzoneRequest(
  state: State,
  { resource }: Boundaries["removeResourceFromPlanzone"]["request"],
): State {
  return state.map((res) => ({
    ...res,
    group: {
      ...res.group,
      resources: group.resources.filter((r) => r.uuid !== resource.uuid),
    },
  }));
}

function handleRemoveResourceFromPlanzoneFailure(
  state: State,
  { resource }: Boundaries["removeResourceFromPlanzone"]["failure"],
): State {
  return state.map((res) => ({
    ...res,
    group: {
      ...res.group,
      resources: group.resources,
    },
  }));
}

function propagateEvent(state = initialState, event: Event): State {
  return state.map((res) => ({
    ...res,
    group: reduceResourceGroup(res.group, event),
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveResourceGroupRequested]: handleRequest,
      [events.retrieveResourceGroupSucceeded]: handleSuccess,
      [events.retrieveResourceGroupFailed]: handleFailure,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
